import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, lastValueFrom, map, Observable, of, share, shareReplay, switchMap, tap } from "rxjs";
import { environment } from "../../environments/environment";
import { FilterChip } from "../models/filter-chip";
import { BaseService } from "./base.service";
import { User } from "../models/user";
import { UIPreference, UserUIPreference } from "../models/ui-preferences";
import { ProjectMembership } from "../models/project";

@Injectable({ providedIn: 'root' })
export class UsersService extends BaseService {
    private sortProperties : { [key: string]: string } = {
        "username": "HawkId", "email": "Email", "fullname": "DisplayName", "reportsto": "ReportsTo", "emp-type": "EmployeeType", "emp-number": "EmployeeNumber", "role" : "Role",
        "last-updated": "LastUpdated", "created": "Created", "tks-begin-date": "TKSBeginDate", "univid": "UniversityId", "leave": "Leave", "pop-source": "PopSource",

        "job-title": "JobTitle", "org-id": "OrgId", "pay-grade": "PayGrade", "dept-code": "DeptCode", "job-code": "JobCode", "subdept-code": "SubdeptCode",
        "former-job-title": "FormerJobTitle", "former-org-id": "FormerOrgId", "former-pay-grade": "FormerPayGrade", "former-dept-code": "FormerDeptCode", "former-job-code": "FormerJobCode", "former-subdept-code": "FormerSubdeptCode",
        
        "finalLeave": "FinalLeave"
    };
    currentUser?: User;
    createUser(user: User): Observable<User> {
        const create = this.http.post<User>(environment.apiUrl + 'user', user).pipe(shareReplay());
        create.subscribe(c => this.sourceUpdatedSubject.next(true));
        return create;
    }

    updateUser(user: User): Observable<User> {
        const update = this.http.put<User>(environment.apiUrl + `user/${user.id}`, user).pipe(shareReplay());
        update.subscribe(c => this.sourceUpdatedSubject.next(true));
        return update;
    }

    deleteUser(user: User): Observable<User> {
        const update = this.http.delete<User>(environment.apiUrl + `user/${user.id}`).pipe(shareReplay());
        update.subscribe(c => this.sourceUpdatedSubject.next(true));
        return update;
    }

    bulkAddRemoveProjects(userId: number, adds: ProjectMembership[], removes: ProjectMembership[]) {
        const update = this.http.put(environment.apiUrl + `user/${userId}/bulk-add-remove`, { adds, removes }).pipe(shareReplay());
        update.subscribe(c => this.sourceUpdatedSubject.next(true));
        return update;
    }

    private sourceUpdatedSubject = new BehaviorSubject<boolean>(false);
    sourceUpdated$ = this.sourceUpdatedSubject.asObservable();

    private projectsSubject = new BehaviorSubject<User[]>([]);
    projects$ = this.projectsSubject.asObservable();

    private preferencesUpdatedSubject = new BehaviorSubject<UIPreference>(<UIPreference><unknown>null);
    preferencesUpdated$ = this.preferencesUpdatedSubject.asObservable();

    constructor(private http: HttpClient) {
        super();
    }

    fetchUsers(effortKey: string) {
        this.http.get<User[]>(environment.apiUrl + 'user/search').subscribe(projects => {
            this.projectsSubject.next(projects);
        });
    }

    getCurrentUser() {
        return this.currentUser ? of(this.currentUser).pipe(shareReplay()) : this.loadCurrentUser();
    }

    loadCurrentUser() {
        return this.http.get<User>(environment.apiUrl + 'user/current').pipe(tap(cu => this.currentUser = cu));
    }

    getTimesheetStatuses(): Observable<string[][]> {
        return this.http.get<string[][]>(environment.apiUrl + 'user/timesheet-statuses');
    }

    getDistinctValuesForProperty(property: string, filter: string): Observable<string[]> {
        return this.http.get<string[]>(environment.apiUrl + `user/distinct-values/${property}?filter=${encodeURIComponent(filter)}`);
    }

    /**
     * 
     * @param filters An array of FilterChips understood as filtering in ANDed fashion.
     * @param pageSize The number of items to return per request. 0 = all.
     * @returns A shared observable of up to pageSize items matching the filter.
     */
    filterUsers(filters: FilterChip[], pageSize: number = 10, pageIndex: number = 0, sortField: string | undefined = undefined, sortDirection: string | undefined = undefined) {
        return this.sourceUpdatedSubject.pipe(
            switchMap(() => {
                const keywords = filters.filter(c => c.chip == null).map(f => f.value).join(' ');
                const propertyValues = filters.reduce((v, c) => { if (c.chip != null) v[c.chip] = c.value; return v; }, <{ [key: string]: string }>{});
                const filter = <any>{ keywords, propertyValues };
                if (pageSize) filter['pageSize'] = ''+pageSize
                if (pageIndex) filter['page'] = ''+pageIndex;
                if (sortField) filter['SortSpecifications[0].Property'] = this.sortProperties[sortField];
                if (sortDirection) filter['SortSpecifications[0].Direction'] = sortDirection == "asc" ? "Ascending" : "Descending";
                const filterQuery = this.serialize(filter);
                console.log(filterQuery, filter);
                return this.http.get<User[]>(environment.apiUrl + 'user/search?' + filterQuery, { observe: 'response'})
                    .pipe(map(response => {
                        const paging = response.headers.get('Content-Range');
                        const total = parseInt(paging?.split("/")[1] ?? '0');
                        const users = response.body ?? [];
                        return { total, users: users.map(u => {
                            u.hawkId = u.hawkId;
                            u.email = u.email;
                            u.groupMemberships = u.groupMemberships;
                            u.displayName = u.displayName; //[u.first ?? '', u.middle ?? '', u.last ?? ''].join(' ');
                            return u;
                        }) };
                    }));
            }),
            share()
        );
    }

    savePreference(pref: UIPreference, userId: number) {
        const userPref = new UserUIPreference();
        userPref.userId = userId;
        userPref.data = JSON.stringify(pref);
        if (this.currentUser) this.currentUser.userUIPreference = userPref;
        return this.http.put<UserUIPreference>(environment.apiUrl + "user/ui-pref", userPref).pipe(
            map(userUiPref => userUiPref.data ? JSON.parse(userUiPref.data) : new UIPreference() ),
            tap(p => this.preferencesUpdatedSubject.next(pref))
        );
    }

    // addUser(newEffort: Effort) {
    //   console.log(newEffort, "soupa");
    //   this.http.post(environment.apiUrl + 'projects', newEffort).subscribe(() => {
    //     this.fetchUsers(); // Re-fetch projects after adding
    //   });
    // }

    // deleteEffort(effort: Effort) {
    //   this.http.delete(environment.apiUrl + `projects/${effort.id}`).subscribe(() => {
    //       this.fetchUsers();
    //   });
    // }
}

export class UserFilter {
    effortId?: number;
    title?: string;
}